import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { AssessmentItem, DayData, ProviderKey, SourceKey, TrainingItem } from './types';
import { PROVIDER_LOGO, SOURCE_LOGO } from './visual';
import {
  fmtKm, fmtMin, fmtPaceMi, fmtSpeed, fmtPct, fmtCm, fmtForce, fmtFloat,
} from './utils/format';

interface Props {
  date: string;
  day: DayData;
  enabledSources: SourceKey[];
  isToday?: boolean;
  focused?: boolean;
  onClick?: () => void;
}

function trainingLines(it: TrainingItem): string[] {
  if (it.kind === 'catapult_session') {
    return [
      [fmtMin(it.duration_min), fmtKm(it.distance_m)].filter(Boolean).join(' · '),
      [
        it.player_load ? `PL ${Math.round(it.player_load)}` : null,
        fmtSpeed(it.max_velocity),
      ].filter(Boolean).join(' · '),
    ].filter(Boolean);
  }
  if (it.kind === 'garmin_daily') {
    return [
      [fmtKm(it.distance_m), it.steps ? `${it.steps.toLocaleString()} steps` : null]
        .filter(Boolean).join(' · '),
      it.hr_avg != null ? `${Math.round(it.hr_avg)} bpm` : '',
    ].filter(Boolean);
  }
  // terra_activity
  const min = it.duration_s ? Math.round(it.duration_s / 60) : null;
  return [
    [fmtKm(it.distance_m), fmtMin(min)].filter(Boolean).join(' · '),
    [
      fmtPaceMi(it.pace_s_per_km),
      it.hr_avg != null ? `${Math.round(it.hr_avg)} bpm` : null,
    ].filter(Boolean).join(' · '),
  ].filter(Boolean);
}

function assessmentLines(it: AssessmentItem): string[] {
  return [
    [fmtCm(it.jump_height_m), fmtForce(it.peak_force_n)].filter(Boolean).join(' · '),
    [
      it.rsi != null ? `RSI ${fmtFloat(it.rsi, 2)}` : null,
      fmtPct(it.lr_braking_pct), fmtPct(it.lr_propulsive_pct),
    ].filter(Boolean).join(' · '),
  ].filter(Boolean);
}

function trainingTitle(it: TrainingItem): string {
  if (it.kind === 'catapult_session') return it.name || 'Session';
  if (it.kind === 'garmin_daily') return 'Daily activity';
  return it.display_label || 'Activity';
}

function trainingProvider(it: TrainingItem): ProviderKey {
  if (it.kind === 'catapult_session') return 'catapult';
  if (it.kind === 'garmin_daily') return 'garmin';
  return it.source as ProviderKey;
}

function bucketFor(it: TrainingItem | AssessmentItem): SourceKey {
  if (it.kind === 'hawkin_test') return 'hawkin';
  if (it.kind === 'catapult_session') return 'catapult';
  if (it.kind === 'garmin_daily') return 'garmin';
  return it.source as SourceKey;
}

export function WeekDayCell({ date, day, enabledSources, isToday, focused, onClick }: Props) {
  const { isDark } = useTheme();
  const [, , dd] = date.split('-');
  const enabledSet = new Set(enabledSources);

  const cellStyle: React.CSSProperties = {
    ...(focused ? {
      // inset — outset is clipped by the grid's overflow-hidden ancestor
      // on the leftmost / rightmost focused cells.
      boxShadow: 'inset 0 0 0 2px var(--text-primary)',
      position: 'relative',
      zIndex: 1,
    } : {}),
  };

  const trainingItems = day.training.filter((it) => enabledSet.has(bucketFor(it)));
  const assessmentItems = day.assessment.filter((it) => enabledSet.has(bucketFor(it)));
  const showWhoopSleep = enabledSet.has('whoop') && day.recovery.sleep;

  return (
    <button
      type="button"
      onClick={onClick}
      data-focused={focused ? 'true' : undefined}
      data-today={isToday ? 'true' : undefined}
      style={cellStyle}
      className="w-full p-2 text-left border border-[var(--card-border)] flex flex-col gap-2 min-h-[160px]"
    >
      <div className="flex items-start">
        {isToday ? (
          <span className="inline-flex w-6 h-6 items-center justify-center text-xs font-semibold text-white bg-red-600 rounded-full">
            {Number(dd)}
          </span>
        ) : (
          <span className="text-sm cgm-text-primary">{Number(dd)}</span>
        )}
      </div>

      {showWhoopSleep && (
        <WeekSection logoSrc={isDark ? SOURCE_LOGO.whoop.dark : SOURCE_LOGO.whoop.light} title="Sleep">
          <div className="text-[11px] cgm-text-secondary">
            {(day.recovery.sleep!.total_minutes / 60).toFixed(1)}h
            {day.recovery.sleep!.quality_score != null ? ` · score ${day.recovery.sleep!.quality_score.toFixed(0)}` : ''}
          </div>
        </WeekSection>
      )}

      {trainingItems.map((it, i) => {
        const prov = trainingProvider(it);
        const logo = PROVIDER_LOGO[prov];
        return (
          <WeekSection
            key={`t-${i}`}
            logoSrc={logo ? (isDark ? logo.dark : logo.light) : undefined}
            title={trainingTitle(it)}
          >
            {trainingLines(it).map((line, j) => (
              <div key={j} className="text-[11px] cgm-text-secondary">{line}</div>
            ))}
          </WeekSection>
        );
      })}

      {assessmentItems.map((it, i) => (
        <WeekSection
          key={`a-${i}`}
          logoSrc={isDark ? SOURCE_LOGO.hawkin.dark : SOURCE_LOGO.hawkin.light}
          title={it.test_type}
        >
          {assessmentLines(it).map((line, j) => (
            <div key={j} className="text-[11px] cgm-text-secondary">{line}</div>
          ))}
        </WeekSection>
      ))}
    </button>
  );
}

function WeekSection({
  logoSrc, title, children,
}: { logoSrc?: string; title: string; children: React.ReactNode }) {
  return (
    <div className="border border-[var(--card-border)] p-1.5">
      <div className="flex items-center gap-1.5 mb-1">
        {logoSrc && <img src={logoSrc} alt="" className="w-3.5 h-3.5 object-contain" />}
        <span className="text-xs font-semibold cgm-text-primary truncate">{title}</span>
      </div>
      {children}
    </div>
  );
}
